// D3 Radar Chart for BodyOS data visualizations
// Great for supplement effectiveness comparison, wellness scores, etc.
import * as d3 from 'd3'
import {
  colors,
  fonts,
  animationConfig,
  defaultDimensions
} from './config'
import {
  createTooltip,
  showTooltipHtml,
  hideTooltip,
  createLegend,
  debounce
} from './utils'

export default class RadarChart {
  constructor(container, options = {}) {
    this.container = typeof container === 'string' ? document.querySelector(container) : container
    this.options = {
      width: defaultDimensions.width,
      height: defaultDimensions.height,
      margins: { top: 80, right: 60, bottom: 60, left: 60 },
      levels: 5,
      maxValue: null, // Auto-detect if null
      showLegend: true,
      showLabels: true,
      showValues: false,
      animate: true,
      responsive: true,
      labelOffset: 1.15,
      ...options
    }

    this.data = []
    this.svg = null
    this.tooltip = null

    if (this.options.responsive) {
      this.handleResize = debounce(() => this.render(), 250)
      window.addEventListener('resize', this.handleResize)
    }
  }

  setData(data) {
    this.data = data
    return this
  }

  render() {
    this.container.innerHTML = ''

    if (!this.data || this.data.length === 0) {
      this.renderEmptyState()
      return this
    }

    const containerWidth = this.container.clientWidth || this.options.width
    const width = Math.min(containerWidth, this.options.width)
    const height = (width / this.options.width) * this.options.height

    // Create SVG
    const svg = d3.select(this.container)
      .append('svg')
      .attr('viewBox', `0 0 ${width} ${height}`)
      .attr('preserveAspectRatio', 'xMidYMid meet')
      .attr('class', 'd3-visualization')
      .style('width', '100%')
      .style('height', 'auto')
      .style('font-family', fonts.family)

    this.svg = svg

    const g = svg.append('g')
      .attr('transform', `translate(${width / 2}, ${height / 2})`)

    this.tooltip = createTooltip(this.container)

    const radius = Math.min(
      width - this.options.margins.left - this.options.margins.right,
      height - this.options.margins.top - this.options.margins.bottom
    ) / 2

    // Prepare data - handle single or multi-series
    const series = Array.isArray(this.data[0]?.values) ? this.data : [{ name: 'Data', values: this.data }]
    const axes = series[0].values.map((d) => d.axis)
    const numAxes = axes.length

    // Calculate max value
    const allValues = series.flatMap((s) => s.values.map((v) => v.value))
    const maxValue = this.options.maxValue || Math.max(...allValues) * 1.1

    // Angle slice
    const angleSlice = (Math.PI * 2) / numAxes

    // Scale for radius
    const rScale = d3.scaleLinear()
      .domain([0, maxValue])
      .range([0, radius])

    // Draw circular grid
    this.drawGrid(g, radius, numAxes, angleSlice)

    // Draw axes
    this.drawAxes(g, axes, radius, angleSlice)

    // Draw data areas
    series.forEach((s, i) => {
      const seriesColor = s.color || colors.chart[i % colors.chart.length]
      this.drawArea(g, s.values, rScale, angleSlice, seriesColor, i)
    })

    // Legend
    if (this.options.showLegend && series.length > 1) {
      const legendItems = series.map((s, i) => ({
        label: s.name,
        color: s.color || colors.chart[i % colors.chart.length]
      }))
      createLegend(g, legendItems, {
        x: -radius,
        y: -radius - 50,
        direction: 'horizontal'
      })
    }

    return this
  }

  drawGrid(g, radius, numAxes, angleSlice) {
    const { levels } = this.options

    // Background circles
    for (let level = 0; level < levels; level += 1) {
      const levelFactor = (level + 1) / levels

      // Circle
      g.append('circle')
        .attr('class', 'grid-circle')
        .attr('r', radius * levelFactor)
        .attr('fill', 'none')
        .attr('stroke', colors.grid)
        .attr('stroke-width', 1)
        .attr('stroke-dasharray', level === levels - 1 ? 'none' : '3,3')
    }

    // Radial lines
    for (let i = 0; i < numAxes; i += 1) {
      const angle = i * angleSlice - Math.PI / 2

      g.append('line')
        .attr('class', 'grid-line')
        .attr('x1', 0)
        .attr('y1', 0)
        .attr('x2', radius * Math.cos(angle))
        .attr('y2', radius * Math.sin(angle))
        .attr('stroke', colors.grid)
        .attr('stroke-width', 1)
    }
  }

  drawAxes(g, axes, radius, angleSlice) {
    if (!this.options.showLabels) return

    axes.forEach((axis, i) => {
      const angle = i * angleSlice - Math.PI / 2
      const x = radius * this.options.labelOffset * Math.cos(angle)
      const y = radius * this.options.labelOffset * Math.sin(angle)

      // Adjust text anchor based on position
      let textAnchor = 'middle'
      if (x < -10) textAnchor = 'end'
      else if (x > 10) textAnchor = 'start'

      g.append('text')
        .attr('class', 'axis-label')
        .attr('x', x)
        .attr('y', y)
        .attr('dy', '0.35em')
        .attr('text-anchor', textAnchor)
        .attr('fill', colors.text)
        .attr('font-size', fonts.sizes.sm)
        .attr('font-family', fonts.family)
        .attr('font-weight', fonts.weights.medium)
        .text(axis.toUpperCase())
    })
  }

  drawArea(g, values, rScale, angleSlice, color, seriesIndex) {
    const points = values.map((d, i) => {
      const angle = i * angleSlice - Math.PI / 2
      return {
        x: rScale(d.value) * Math.cos(angle),
        y: rScale(d.value) * Math.sin(angle),
        value: d.value,
        axis: d.axis
      }
    })

    // Line generator
    const radarLine = d3.lineRadial()
      .radius((d) => rScale(d.value))
      .angle((d, i) => i * angleSlice)
      .curve(d3.curveLinearClosed)

    // Area fill
    const areaPath = g.append('path')
      .datum(values)
      .attr('class', `radar-area-${seriesIndex}`)
      .attr('d', radarLine)
      .attr('fill', color)
      .attr('fill-opacity', 0.15)
      .attr('stroke', color)
      .attr('stroke-width', 2)

    if (this.options.animate) {
      areaPath
        .attr('opacity', 0)
        .transition()
        .duration(animationConfig.duration)
        .delay(seriesIndex * 200)
        .attr('opacity', 1)
    }

    // Points
    const pointsGroup = g.selectAll(`.radar-point-${seriesIndex}`)
      .data(points)
      .enter()
      .append('circle')
      .attr('class', `radar-point radar-point-${seriesIndex}`)
      .attr('cx', (d) => d.x)
      .attr('cy', (d) => d.y)
      .attr('r', 0)
      .attr('fill', colors.neutral.white)
      .attr('stroke', color)
      .attr('stroke-width', 2)
      .style('cursor', 'pointer')

    if (this.options.animate) {
      pointsGroup.transition()
        .delay(animationConfig.duration + seriesIndex * 200)
        .duration(300)
        .attr('r', 4)
    } else {
      pointsGroup.attr('r', 4)
    }

    // Point interactions
    pointsGroup
      .on('mouseover', (event, d) => {
        d3.select(event.target)
          .transition()
          .duration(200)
          .attr('r', 6)

        const content = `<strong>${d.axis}</strong><br/><span style="color: ${colors.accent}">${d.value}</span>`
        showTooltipHtml(this.tooltip, content, event)
      })
      .on('mouseout', (event) => {
        d3.select(event.target)
          .transition()
          .duration(200)
          .attr('r', 4)
        hideTooltip(this.tooltip)
      })

    // Value labels
    if (this.options.showValues) {
      g.selectAll(`.radar-value-${seriesIndex}`)
        .data(points)
        .enter()
        .append('text')
        .attr('class', `radar-value radar-value-${seriesIndex}`)
        .attr('x', (d) => d.x * 1.1)
        .attr('y', (d) => d.y * 1.1)
        .attr('dy', '0.35em')
        .attr('text-anchor', 'middle')
        .attr('fill', color)
        .attr('font-size', fonts.sizes.xs)
        .attr('font-family', fonts.family)
        .text((d) => d.value)
    }
  }

  renderEmptyState() {
    const emptyDiv = document.createElement('div')
    emptyDiv.className = 'd3-empty-state'
    emptyDiv.innerHTML = `
      <div style="
        display: flex;
        align-items: center;
        justify-content: center;
        height: 200px;
        border: 2px dashed ${colors.neutral[300]};
        color: ${colors.textMuted};
        font-family: ${fonts.family};
        font-size: ${fonts.sizes.base}px;
      ">
        NO DATA AVAILABLE
      </div>
    `
    this.container.appendChild(emptyDiv)
  }

  destroy() {
    if (this.handleResize) {
      window.removeEventListener('resize', this.handleResize)
    }
    if (this.container) {
      this.container.innerHTML = ''
    }
  }
}
